import { useState, useEffect, useMemo } from "react";
import { Btn, Card, Checkbox, FilterSelect, Icon, Input, Pill, T, TimeZonePicker, toneColors } from "@v0y4ge/jrni-ui";
import { Screen, PageHeader } from "../../shell/chrome";
import { useIsMobile } from "../../shell/useResponsive";
import { can } from "../../shell/access";
import { useNav } from "../../shell/router";
import { StatTile, FilterBar, EmptyState, RowActions } from "../../shell/composites";
import { SharedWithCoord } from "../../shell/list-composites";
import { thStyle, tdStyle, viewBtnStyle } from "../events/EventCard";
import { useLocations, LOCATIONS_REQUEST_OPTS } from "./useLocations";
import { duplicateLocation, archiveLocation, updateLocation } from "@v0y4ge/api-client";
import { toast } from "../../shell/toast";
import { locationOpenNow, type UILocation, type OpenState } from "./locations-adapter";
import { addressFormatFor, downloadText, IMPORT_FIELDS } from "./location-validation";
import { useT, t as i18nT } from "../../lib/i18n";

/**
 * Locations — list screen.
 *
 * Rebuilt 2026-08-07 (Nick) from `Locations - Admin & Bulk Creation.dc.html`
 * option 1a, for the System Administrator persona in all three verticals.
 * Three things the old list didn't do, all of which only matter at estate
 * scale (Maison Rivière alone spans six countries and five time zones):
 *
 *  · **Live local time per row.** "Is Tokyo open right now" is the question an
 *    administrator actually scans for, and it should not require arithmetic.
 *  · **Country facets** rather than one flat list, plus a "needs attention"
 *    queue (hours not set / no time zone / archived) that IS the work list.
 *  · **Real bulk actions** on a selection — set time zone, copy hours from
 *    another location, export, archive. Every one awaits its typed op.
 *
 * The cards view is kept: it predates this design and nothing asked for its
 * removal.
 */
const CARD_GRID = "repeat(auto-fill, minmax(280px, 1fr))";

/* Needs-attention facet tints. The canonical DS tone pairs (`toneColors`) rather
   than the hand-picked hexes this screen shipped with: the literals held ONE
   appearance, so a selected "No time zone" row stayed near-white in dark mode
   with light ink on it. Both halves of each pair invert with the theme. */
const WARN_TINT = toneColors("warning");
const DANGER_TINT = toneColors("danger");

function openPill(l: UILocation) {
  const st: OpenState = locationOpenNow(l);
  if (st === "open") return <Pill tone="success" dot>{i18nT("locations.list.openNow", "Open now")}</Pill>;
  if (st === "closed") return <Pill tone="neutral" dot>{i18nT("locations.list.closed", "Closed")}</Pill>;
  return <Pill tone="warn" dot>{i18nT("locations.list.hoursNotSet", "Hours not set")}</Pill>;
}

const raw = (l: UILocation) => (l.raw ?? {}) as Record<string, unknown>;
const str = (l: UILocation, k: string) => String(raw(l)[k] ?? "");

function zoneClock(tz: string): string {
  if (!tz) return "—";
  try { return new Intl.DateTimeFormat("en-GB", { timeZone: tz, hour: "2-digit", minute: "2-digit", hour12: false }).format(new Date()); }
  catch { return "—"; }
}
function zoneOffset(tz: string): string {
  if (!tz) return "";
  try {
    const p = new Intl.DateTimeFormat("en-US", { timeZone: tz, timeZoneName: "shortOffset" })
      .formatToParts(new Date()).find((x) => x.type === "timeZoneName");
    return p ? p.value.replace("GMT", "UTC") : "";
  } catch { return ""; }
}

/** Needs-attention labels. Functions, not constants — `t()` must never be
 *  called at module scope (that freezes one locale for the session). */
const FLAG_LABEL = {
  hours: () => i18nT("locations.list.hoursNotSet", "Hours not set"),
  tz: () => i18nT("locations.list.noTimeZone", "No time zone"),
  archived: () => i18nT("locations.adapter.archived", "Archived"),
};

export default function LocationsList() {
  const t = useT();
  const nav = useNav();
  const { locations, loading, error, reload } = useLocations();
  const [view, setView] = useState<"list" | "cards">(() => { try { return (localStorage.getItem("locations:view") as "list" | "cards") || "list"; } catch { return "list"; } });
  const setViewPersist = (m: "list" | "cards") => { setView(m); try { localStorage.setItem("locations:view", m); } catch { /* noop */ } };
  const [query, setQuery] = useState("");
  const [country, setCountry] = useState<string | null>(null);
  const [flag, setFlag] = useState<"hours" | "tz" | "archived" | null>(null);
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  const [sel, setSel] = useState<Record<string, true>>({});
  const [busy, setBusy] = useState(false);
  const [tzOpen, setTzOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [, setTick] = useState(0);
  /* Below ~1100px the 198px facet rail leaves a 7-column table 446px to live in,
     which hid the live-clock column and the row actions behind an inner scroll
     (verifier, 2026-08-07). The facets become FilterBar dropdowns instead and
     the table gets the whole width back. */
  const narrow = useIsMobile(1100);

  /* The clocks are the reason this column earns its width — keep them true. */
  useEffect(() => {
    const h = setInterval(() => setTick((n) => n + 1), 30000);
    return () => clearInterval(h);
  }, []);

  const canWrite = can("locations.write");
  const staffAt = (l: UILocation) => (raw(l).staffCount as number | undefined) ?? 0;
  const resAt = (l: UILocation) => l.roomsCount;

  const onSort = (key: string) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(key); setSortDir(key === "name" || key === "address" ? "asc" : "desc"); }
  };

  const facets = useMemo(() => {
    const counts = new Map<string, number>();
    for (const l of locations) {
      const cc = str(l, "country").toUpperCase() || "—";
      counts.set(cc, (counts.get(cc) ?? 0) + 1);
    }
    return [...counts.entries()]
      .map(([cc, n]) => ({ cc, n, name: cc === "—" ? i18nT("locations.list.noCountrySet", "No country set") : addressFormatFor(cc).name }))
      .sort((a, b) => b.n - a.n || a.name.localeCompare(b.name));
  }, [locations]);

  const flagCounts = useMemo(() => ({
    hours: locations.filter((l) => locationOpenNow(l) === "unknown").length,
    tz: locations.filter((l) => !str(l, "timezone")).length,
    archived: locations.filter((l) => str(l, "status") === "archived").length,
  }), [locations]);

  const filtered = locations.filter((l) => {
    if (query.trim() && !`${l.name} ${l.address || ""} ${str(l, "externalCode")} ${str(l, "city")}`.toLowerCase().includes(query.toLowerCase())) return false;
    if (country && (str(l, "country").toUpperCase() || "—") !== country) return false;
    if (flag === "hours" && locationOpenNow(l) !== "unknown") return false;
    if (flag === "tz" && str(l, "timezone")) return false;
    if (flag === "archived" && str(l, "status") !== "archived") return false;
    return true;
  });

  const sorted = (() => {
    if (!sortKey) return filtered;
    const num = sortKey === "staff" || sortKey === "resources";
    const val = (l: UILocation) => sortKey === "staff" ? staffAt(l) : sortKey === "resources" ? resAt(l) : sortKey === "tz" ? str(l, "timezone") : (l as unknown as Record<string, unknown>)[sortKey];
    return [...filtered].sort((a, b) => {
      const r = num ? (Number(val(a)) || 0) - (Number(val(b)) || 0) : String(val(a) || "").localeCompare(String(val(b) || ""));
      return sortDir === "desc" ? -r : r;
    });
  })();

  const selected = sorted.filter((l) => sel[l.id]);
  const selCount = selected.length;
  const clearSel = () => setSel({});
  const toggleSel = (id: string) => setSel((s) => { const n = { ...s }; if (n[id]) delete n[id]; else n[id] = true; return n; });

  /* ── Row actions (all awaited; no optimistic success) ─────────────────── */
  const dupLocation = async (l: UILocation) => {
    try { await duplicateLocation(l.id, LOCATIONS_REQUEST_OPTS); reload(); toast({ tone: "success", title: t("locations.list.locationDuplicated", "Location duplicated"), subtitle: i18nT("locations.list.aCopyOfLNameWasCreated.icu", { value0: l.name }) }); }
    catch (e) { toast({ tone: "danger", title: t("locations.list.couldnTDuplicate", "Couldn't duplicate"), subtitle: e instanceof Error ? e.message : String(e) }); }
  };
  const removeLocation = async (l: UILocation) => {
    try { await archiveLocation(l.id, { archived: true }, LOCATIONS_REQUEST_OPTS); reload(); toast({ tone: "warn", title: t("locations.list.locationArchived", "Location archived"), subtitle: t("locations.list.hiddenFromNewEventsExistingEventsKeepIt", "Hidden from new events. Existing events keep it.") }); }
    catch (e) { toast({ tone: "danger", title: t("locations.list.couldnTArchive", "Couldn't archive"), subtitle: e instanceof Error ? e.message : String(e) }); }
  };

  /* ── Bulk actions ─────────────────────────────────────────────────────── */
  const runBulk = async (label: string, fn: (l: UILocation) => Promise<unknown>) => {
    if (busy || !selCount) return;
    setBusy(true);
    let ok = 0;
    const failures: string[] = [];
    for (const l of selected) {
      try { await fn(l); ok++; }
      catch (e) { failures.push(`${l.name}: ${e instanceof Error ? e.message : String(e)}`); }
    }
    setBusy(false);
    reload();
    clearSel();
    if (!failures.length) toast({ tone: "success", title: label, subtitle: `${ok} ${ok === 1 ? t("locations.list.location", "location") : t("locations.list.locations", "Locations").toLowerCase()}` });
    else if (!ok) toast({ tone: "danger", title: t("locations.list.nothingChanged", "Nothing changed"), subtitle: failures[0] });
    else toast({ tone: "warn", title: `${label} — ${ok} ${t("locations.list.ofN", "of")} ${ok + failures.length}`, subtitle: failures[0] });
  };

  const bulkTimeZone = (tz: string) => {
    setTzOpen(false);
    void runBulk(t("locations.list.timeZoneUpdated", "Time zone updated"), (l) =>
      (updateLocation as unknown as (id: string, b: unknown, o: unknown) => Promise<unknown>)(l.id, { timezone: tz }, LOCATIONS_REQUEST_OPTS));
  };

  const bulkCopyHours = (sourceId: string) => {
    const src = locations.find((l) => l.id === sourceId);
    if (!src) return;
    const hours = raw(src).hours;
    if (!Array.isArray(hours) || !hours.length) {
      toast({ tone: "warn", title: t("locations.list.thatLocationHasNoHours", "That location has no hours to copy"), subtitle: src.name });
      return;
    }
    void runBulk(t("locations.list.hoursCopied", "Hours copied"), (l) =>
      (updateLocation as unknown as (id: string, b: unknown, o: unknown) => Promise<unknown>)(l.id, { hours, hoursSet: true }, LOCATIONS_REQUEST_OPTS));
  };

  const bulkArchive = () => void runBulk(t("locations.list.locationsArchived", "Locations archived"), (l) =>
    archiveLocation(l.id, { archived: true }, LOCATIONS_REQUEST_OPTS));

  const exportRows = (list: UILocation[]) => {
    const esc = (s: string) => (/[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s);
    const cols = IMPORT_FIELDS.map((f) => f.key);
    const lines = [IMPORT_FIELDS.map((f) => f.label).join(",")];
    for (const l of list) lines.push(cols.map((c) => esc(str(l, c))).join(","));
    downloadText("locations.csv", lines.join("\r\n"));
    toast({ tone: "success", title: t("locations.list.exported", "Exported"), subtitle: `${list.length} ${t("locations.list.rows", "rows")}` });
  };

  const actions = (l: UILocation) => ({
    testid: "locations-item",
    onView: () => nav.go("locations.edit", { id: l.id, view: 1 }),
    viewLabel: "View details",
    onEdit: canWrite ? () => nav.go("locations.edit", { id: l.id }) : undefined,
    editLabel: "Edit location",
    onDuplicate: canWrite ? () => void dupLocation(l) : undefined,
    moreHeader: l.name,
    moreItems: canWrite ? [{ icon: <Icon.trash size={14} />, label: t("locations.list.archiveLocation", "Archive location"), danger: true, sub: "Hides from new events", onClick: () => void removeLocation(l) }] : [],
  });

  const SortTh = ({ label, k, width, align }: { label: string; k: string; width?: number; align?: "right" }) => {
    const active = sortKey === k;
    return (
      <th style={{ ...thStyle, ...(width ? { width } : {}), ...(align === "right" ? { textAlign: "right" as const } : {}), cursor: "pointer", userSelect: "none", color: active ? T.n1 : undefined }}
        onClick={() => onSort(k)} title={i18nT("locations.list.sortByLabel.icu", { value0: label })} aria-sort={active ? (sortDir === "asc" ? "ascending" : "descending") : "none"}>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 4, justifyContent: align === "right" ? "flex-end" : "flex-start" }}>
          {label}<span style={{ display: "inline-flex", opacity: active ? 1 : 0.25 }}><Icon.chev dir={active ? (sortDir === "asc" ? "up" : "down") : "down"} size={11} /></span>
        </span>
      </th>
    );
  };

  const openCount = locations.filter((l) => locationOpenNow(l) === "open").length;
  const zoneCount = new Set(locations.map((l) => str(l, "timezone")).filter(Boolean)).size;

  /* ui-raw: a facet row is not a Button — it is a filter row (status dot · label ·
     count) whose whole box is the tint that carries the "needs attention" state.
     `Btn variant="ghost"` was tried and REVERTED: it forces height/gap/radius/weight
     that every call site then has to override back (height:"auto", border:"none"),
     and its CSS hover layer cannot apply anyway because the inline tint background
     outranks the stylesheet — so the wrapper buys states it can't show. The reset's
     `*:focus-visible` ring already reaches this native <button>. Missing component
     filed: a DS facet/count row (see report). */
  // ui-raw: filter/count row, not a Button — see the note above.
  const facetRow = (key: string, label: string, n: number, on: boolean, onClick: () => void, tint?: { bg: string; fg: string; dot: string }) => (
    <button key={key} type="button" onClick={onClick}
      style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", padding: "7px 10px", borderRadius: 7, border: "none", cursor: "pointer", textAlign: "left", fontFamily: T.font, background: on ? T.p4 : (tint?.bg ?? "transparent") }}>
      {tint ? <span style={{ width: 6, height: 6, borderRadius: "50%", background: tint.dot, flexShrink: 0 }} /> : null}
      <span style={{ flex: 1, minWidth: 0, fontSize: 12.5, fontWeight: on ? 600 : 500, color: on ? T.p1 : (tint?.fg ?? T.n2), whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{label}</span>
      <span style={{ fontSize: 11.5, fontFamily: T.fontMono, color: on ? T.p1 : (tint?.fg ?? T.soft) }}>{n}</span>
    </button>
  );

  return (
    <Screen rail="locations">
      <PageHeader
        title={t("locations.list.locations", "Locations")}
        subtitle={i18nT("locations.list.venuesSubtitleShort", "Hours, time zone and who works at each venue.")}
        breadcrumb={null}
        actions={<div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
          {/* ui-raw: `SegmentedControl` is the right component and its `md` size already
              matches this control's metrics exactly (h34 · px12 · fs13), but it renders an
              INSET TRACK (3px pad, 2px gap, n6 fill, radius md) where this is a flush
              joined pair with a hairline divider, radius 4 and a chrome shadow — a restyle,
              not a swap. The geometry lives in `viewBtnStyle`, SHARED from ../events/EventCard
              with the other list screens, so converging it is a fleet decision, not this
              screen's to make unilaterally. */}
          <div style={{ display: "inline-flex", border: `1px solid ${T.border}`, borderRadius: 4, overflow: "hidden", boxShadow: T.shadowChrome }}>
            {/* ui-raw: flush joined pair, not SegmentedControl's inset track — note above. */}
            <button style={viewBtnStyle(view === "list")} onClick={() => setViewPersist("list")}>{t("locations.list.list", "≡ List")}</button>
            {/* ui-raw: same pair — note above. */}
            <button style={viewBtnStyle(view === "cards")} onClick={() => setViewPersist("cards")}>{t("locations.list.cards", "⊞ Cards")}</button>
          </div>
          {/* Split button — design option 1a. One primary action plus a caret menu;
              three flat top-level buttons pushed the header past the viewport and
              clipped this very control (verifier, 2026-08-07). */}
          <div style={{ position: "relative", display: "inline-flex" }}>
            <div style={{ display: "inline-flex", alignItems: "stretch" }}>
              {canWrite ? (
                <Btn icon={<Icon.plus />} onClick={() => nav.go("locations.edit", { new: 1 })} style={{ borderTopRightRadius: 0, borderBottomRightRadius: 0 }}>
                  {t("locations.list.addLocation", "Add location")}
                </Btn>
              ) : null}
              <Btn
                variant={canWrite ? undefined : "secondary"}
                onClick={() => setAddOpen((v) => !v)}
                title={t("locations.list.moreWaysToAdd", "More ways to add")}
                style={canWrite
                  ? { borderTopLeftRadius: 0, borderBottomLeftRadius: 0, paddingLeft: 9, paddingRight: 9, marginLeft: 1 }
                  : { paddingLeft: 10, paddingRight: 10 }}>
                <Icon.chev dir={addOpen ? "up" : "down"} size={12} />
              </Btn>
            </div>
            {addOpen ? (
              <>
                <div onClick={() => setAddOpen(false)} style={{ position: "fixed", inset: 0, zIndex: T.z.dropdown }} />
                <div style={{ position: "absolute", top: 42, right: 0, zIndex: T.z.dropdown, width: 268, background: T.white, border: `1px solid ${T.border}`, borderRadius: 10, boxShadow: T.shadowMd, padding: 6 }}>
                  {([
                    { id: "bulk", label: t("locations.list.addInBulk", "Add in bulk"), sub: t("locations.list.uploadPasteTypeOrClone", "Upload, paste, type or clone"), icon: <Icon.upload size={14} />, show: canWrite, run: () => nav.go("locations.import") },
                    { id: "clone", label: t("locations.list.cloneALocation", "Clone a location"), sub: t("locations.list.copyOneSiteNTimes", "Copy one site, several times"), icon: <Icon.copy size={14} />, show: canWrite, run: () => nav.go("locations.import") },
                    { id: "export", label: t("locations.list.exportCsv", "Export CSV"), sub: i18nT("locations.list.everyRowCurrentlyShown", "Every row currently shown"), icon: <Icon.download size={14} />, show: true, run: () => exportRows(sorted) },
                  ]).filter((r) => r.show).map((r) => (
                    /* ui-raw: `Menu`/`MenuItem` owns its own open state and needs a `trigger`,
                       but this caret is the right half of a split button, and `MenuItem` has no
                       supporting-text slot. `OverflowMenuItem` DOES take `sub`, but drops the
                       28px tinted icon tile for a bare soft glyph and renders 13/500 where this
                       is 13.5/600 — visibly lighter. Both are restyles. */
                    <button key={r.id} onClick={() => { setAddOpen(false); r.run(); }}
                      style={{ display: "flex", alignItems: "center", gap: 10, width: "100%", padding: "9px 10px", borderRadius: 7, border: "none", background: "transparent", cursor: "pointer", textAlign: "left", fontFamily: T.font }}>
                      <span style={{ width: 28, height: 28, borderRadius: 7, background: T.p4, color: T.p1, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>{r.icon}</span>
                      <span style={{ flex: 1, minWidth: 0 }}>
                        <span style={{ display: "block", fontSize: 13.5, fontWeight: 600, color: T.n1 }}>{r.label}</span>
                        <span style={{ display: "block", fontSize: 11.5, color: T.soft, marginTop: 1 }}>{r.sub}</span>
                      </span>
                    </button>
                  ))}
                </div>
              </>
            ) : null}
          </div>
        </div>}
      />

      <div style={{ padding: "16px 24px 0", display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12 }}>
        <StatTile label={t("locations.list.locations", "Locations")} value={locations.length} icon={<Icon.loc size={14} />} accent={T.p1} />
        <StatTile label={t("locations.list.openRightNow", "Open right now")} value={openCount} icon={<Icon.clock size={14} />} accent={T.green1} />
        <StatTile label={t("locations.list.countries", "Countries")} value={facets.length} icon={<Icon.grid size={14} />} accent={T.tDarkBlue} />
        <StatTile label={t("locations.list.timeZones", "Time zones")} value={zoneCount} icon={<Icon.clock size={14} />} accent={T.p1} />
      </div>

      <FilterBar>
        <Input full={false} leading={<Icon.search size={14} />} placeholder={t("locations.list.searchNameCityOrCode", "Search name, city, or store code…")} value={query} onChange={(e) => setQuery(e.target.value)} size="sm" style={{ width: 260 }} />
        {query.trim() || country || flag ? (
          /* ui-raw: a pill-radius text reset, not a Button — `Btn variant="link"` is 28px
             tall with control radius and would need height/radius/padding all overridden. */
          <button onClick={() => { setQuery(""); setCountry(null); setFlag(null); }} style={{ padding: "6px 10px", borderRadius: 999, fontSize: T.text.label.fontSize, fontWeight: 500, background: "transparent", color: T.p1, border: "none", cursor: "pointer", fontFamily: T.font }}>{t("common.actions.clear", "Clear")}</button>
        ) : null}
        {narrow ? (
          <>
            <FilterSelect
              label={country ? (facets.find((f) => f.cc === country)?.name ?? country) : t("locations.list.anyCountry", "Any country")}
              options={facets.map((f) => `${f.name} (${f.n})`)}
              active={!!country}
              onPick={(o) => { const name = o.replace(/ \(\d+\)$/, ""); setCountry(facets.find((f) => f.name === name)?.cc ?? null); }}
              onClear={() => setCountry(null)}
              anyLabel={i18nT("locations.list.anyCountry", "Any country")}
            />
            <FilterSelect
              label={flag ? FLAG_LABEL[flag]() : t("locations.list.anyState", "Any state")}
              options={[`${FLAG_LABEL.hours()} (${flagCounts.hours})`, `${FLAG_LABEL.tz()} (${flagCounts.tz})`, `${FLAG_LABEL.archived()} (${flagCounts.archived})`]}
              active={!!flag}
              onPick={(o) => {
                const name = o.replace(/ \(\d+\)$/, "");
                setFlag(name === FLAG_LABEL.hours() ? "hours" : name === FLAG_LABEL.tz() ? "tz" : "archived");
              }}
              onClear={() => setFlag(null)}
              anyLabel={i18nT("locations.list.anyState", "Any state")}
            />
          </>
        ) : null}
        <span style={{ marginLeft: "auto", display: "inline-flex", alignItems: "center", gap: 10 }}>
          <SharedWithCoord object="location list" note="Event Day's location picker reads the same locationId set." />
          <span style={{ fontSize: T.text.label.fontSize, color: T.soft }}>{sorted.length} {t("locations.list.of", "of")}{" "}{locations.length}</span>
        </span>
      </FilterBar>

      <div style={{ flex: 1, minHeight: 0, display: "flex", gap: 16, padding: "0 24px 24px" }}>
        {/* Facets — wide viewports only; below 1100px they ride in the FilterBar. */}
        {locations.length && !error && !narrow ? (
          <div style={{ width: 198, flexShrink: 0, display: "flex", flexDirection: "column", gap: 12 }}>
            <Card style={{ padding: "10px 6px" }}>
              <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: ".08em", textTransform: "uppercase", color: T.soft, padding: "0 10px 6px" }}>{t("locations.list.country", "Country")}</div>
              {facets.map((f) => facetRow(f.cc, f.name, f.n, country === f.cc, () => setCountry(country === f.cc ? null : f.cc)))}
            </Card>
            <Card style={{ padding: "10px 6px" }}>
              <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: ".08em", textTransform: "uppercase", color: T.soft, padding: "0 10px 6px" }}>{t("locations.list.needsAttention", "Needs attention")}</div>
              {facetRow("hours", t("locations.list.hoursNotSet", "Hours not set"), flagCounts.hours, flag === "hours", () => setFlag(flag === "hours" ? null : "hours"), { bg: WARN_TINT.bg, fg: WARN_TINT.fg, dot: T.orange1 })}
              {facetRow("tz", t("locations.list.noTimeZone", "No time zone"), flagCounts.tz, flag === "tz", () => setFlag(flag === "tz" ? null : "tz"), { bg: DANGER_TINT.bg, fg: DANGER_TINT.fg, dot: T.red1 })}
              {facetRow("archived", t("locations.adapter.archived", "Archived"), flagCounts.archived, flag === "archived", () => setFlag(flag === "archived" ? null : "archived"), { bg: T.n6, fg: T.n2, dot: T.n3 })}
            </Card>
          </div>
        ) : null}

        <div style={{ flex: 1, minWidth: 0, overflow: "auto" }}>
          {error ? (
            <EmptyState variant="card" tone="danger" icon={<Icon.warn size={20} />} title={t("locations.list.couldnTLoadLocations", "Couldn't load locations")} body={error} action={<Btn variant="secondary" onClick={reload}>{t("common.actions.retry", "Retry")}</Btn>} />
          ) : loading ? (
            <div style={{ padding: 40, textAlign: "center", color: T.soft, fontSize: T.text.body.fontSize }}>{t("locations.list.loadingLocations", "Loading locations…")}</div>
          ) : sorted.length === 0 ? (
            <EmptyState variant="card" icon={<Icon.loc size={20} />} title={t("locations.list.noLocationsMatch", "No locations match")} body={t("locations.list.tryADifferentSearchOrAddALocation", "Try a different search, or add a location.")}
              action={canWrite ? <Btn icon={<Icon.plus size={14} />} onClick={() => nav.go("locations.edit", { new: 1 })}>{t("locations.list.addLocation", "Add location")}</Btn> : null} />
          ) : view === "cards" ? (
            <div style={{ display: "grid", gridTemplateColumns: CARD_GRID, gap: 16 }}>
              {sorted.map((l) => (
                <Card key={l.id} dataId={l.id} onClick={() => actions(l).onView()} style={{ padding: 0, display: "flex", flexDirection: "column", cursor: "pointer", overflow: "hidden" }}>
                  <div style={{ padding: "16px 18px 14px", flex: 1, display: "flex", flexDirection: "column", gap: 12 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 11 }}>
                      <span style={{ width: 38, height: 38, borderRadius: 8, background: T.p3, color: T.p1, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><Icon.loc size={17} /></span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: T.text.h3.fontSize, fontWeight: 700, color: T.n1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{l.name}</div>
                        <div style={{ fontSize: T.text.label.fontSize, color: T.soft, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{l.address || "No address"}</div>
                      </div>
                    </div>
                    <div>{openPill(l)}</div>
                    <div style={{ marginTop: "auto", paddingTop: 10, borderTop: `1px solid ${T.border}`, display: "flex", gap: 16, fontSize: T.text.label.fontSize, color: T.soft }}>
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}><Icon.clock size={12} /> <b style={{ color: T.n1, fontVariantNumeric: "tabular-nums" }}>{zoneClock(str(l, "timezone"))}</b> {zoneOffset(str(l, "timezone"))}</span>
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}><Icon.grid size={12} /> <b style={{ color: T.n1 }}>{resAt(l)}</b> {resAt(l) === 1 ? t('locations.list.text') : t('locations.list.text2')}</span>
                    </div>
                  </div>
                  <div style={{ borderTop: `1px solid ${T.border}`, padding: "8px 12px", display: "flex", justifyContent: "flex-end" }} onClick={(e) => e.stopPropagation()}>
                    <RowActions {...actions(l)} />
                  </div>
                </Card>
              ))}
            </div>
          ) : (
            <Card style={{ padding: 0 }}>
              {/* Bulk bar */}
              {selCount ? (
                <div style={{ display: "flex", alignItems: "center", gap: 9, padding: "9px 14px", background: T.p4, borderBottom: `1px solid ${T.p2}`, flexWrap: "wrap", position: "relative" }}>
                  <span style={{ fontSize: T.text.bodySm.fontSize, fontWeight: 600, color: T.p1, whiteSpace: "nowrap" }}>
                    {selCount} {selCount === 1 ? t("locations.list.locationSelected", "location selected") : t("locations.list.locationsSelected", "locations selected")}
                  </span>
                  <span style={{ width: 1, height: 18, background: T.p2 }} />

                  <div style={{ position: "relative" }}>
                    <Btn size="sm" variant="secondary" disabled={busy} icon={<Icon.clock size={12} />} onClick={() => setTzOpen((v) => !v)}>
                      {t("locations.list.setTimeZone", "Set time zone")}
                    </Btn>
                    {tzOpen ? (
                      <div style={{ position: "absolute", top: 36, left: 0, zIndex: T.z.dropdown, width: 300, background: T.white, border: `1px solid ${T.border}`, borderRadius: 10, boxShadow: T.shadowMd, padding: 10 }}>
                        <div style={{ fontSize: T.text.label.fontSize, color: T.soft, marginBottom: 7 }}>
                          {t("locations.list.appliedToEverySelectedLocation", "Applied to every selected location — and to everyone who signs in there.")}
                        </div>
                        <TimeZonePicker value="" onChange={(v: string) => bulkTimeZone(v)} />
                      </div>
                    ) : null}
                  </div>

                  {/* ui-raw: no DS picker fits this slot. `Dropdown` is the right component but
                      ships ONE trigger height (40px, `selectTriggerHeight("md")`) with no `size`
                      prop, so it stands 12px taller than the `Btn size="sm"` (28px) controls it
                      sits between and grew this bar 625→635px — measured, not assumed. `Select`
                      does take `size="sm"` but is a display-only trigger with no menu, so using
                      it would mean hand-rolling the option list back. Blocking ask filed: give
                      `Dropdown` an OPTIONAL `size?: "sm" | "md"` (default "md" — today's exact
                      rendering) threaded into the `selectBase` trigger it already shares with
                      FilterSelect, then this becomes a one-line swap. */}
                  {/* ui-raw: Dropdown has no size="sm" — measured note above. */}
                  <select disabled={busy} value="" onChange={(e) => { if (e.target.value) bulkCopyHours(e.target.value); e.target.value = ""; }}
                    aria-label={i18nT("locations.list.copyHoursFrom", "Copy hours from…")}
                    style={{ height: 30, borderRadius: T.radius.sm, border: `1px solid ${T.n4}`, background: T.white, color: T.n2, fontSize: 12.5, fontFamily: T.font, padding: "0 8px", maxWidth: 200 }}>
                    <option value="">{i18nT("locations.list.copyHoursFrom", "Copy hours from…")}</option>
                    {locations.filter((l) => Array.isArray(raw(l).hours) && (raw(l).hours as unknown[]).length).map((l) => (
                      <option key={l.id} value={l.id}>{l.name}</option>
                    ))}
                  </select>

                  <Btn size="sm" variant="secondary" disabled={busy} icon={<Icon.download size={12} />} onClick={() => exportRows(selected)}>{t("common.actions.export", "Export")}</Btn>
                  <Btn size="sm" variant="secondary" disabled={busy} icon={<Icon.trash size={12} />} onClick={bulkArchive}>{t("locations.list.archive", "Archive")}</Btn>
                  <span style={{ flex: 1 }} />
                  {/* ui-raw: bare text action inside the bulk bar — same reason as the filter
                      Clear above; `Btn` geometry would be overridden away entirely. */}
                  <button onClick={clearSel} style={{ fontSize: 12.5, fontWeight: 500, color: T.soft, background: "transparent", border: "none", cursor: "pointer", fontFamily: T.font }}>{t("common.actions.clear", "Clear")}</button>
                </div>
              ) : null}

              {/* ui-raw: `Table`/`DataTable` cannot reproduce this one byte-for-byte — the
                  cell chrome comes from `thStyle`/`tdStyle` SHARED from ../events/EventCard,
                  the sort affordance is a custom chevron that dims to 0.25 when inactive, and
                  the column set DROPS the Staff column below 1100px while folding it into the
                  name cell. Adopting Table here would restyle padding, borders, the sort icon
                  and the selection tint. Fleet decision, tracked in the report. */}
              <table style={{ width: "100%", tableLayout: "fixed", borderCollapse: "collapse", fontFamily: T.font, fontSize: T.text.bodySm.fontSize }}>
                <thead>
                  <tr style={{ background: T.n6, color: T.soft, fontSize: 11, textTransform: "uppercase", fontWeight: 600 }}>
                    <th style={{ ...thStyle, width: 34 }}>
                      <Checkbox
                        checked={selCount > 0 && selCount === sorted.length}
                        onChange={(next) => setSel(next ? Object.fromEntries(sorted.map((l) => [l.id, true as const])) : {})}
                        aria-label={t("locations.list.selectAll", "Select all")}
                      />
                    </th>
                    <SortTh label={t("locations.list.location", "Location")} k="name" />
                    <SortTh label={t("locations.list.address", "Address")} k="address" />
                    <SortTh label={t("locations.list.timeZoneLocalNow", "Time zone · local now")} k="tz" width={narrow ? 130 : 168} />
                    <th style={{ ...thStyle, width: narrow ? 104 : 124 }}>{t("locations.list.hours", "Hours")}</th>
                    {narrow ? null : <SortTh label={t("locations.list.staff", "Staff")} k="staff" width={64} />}
                    <th style={{ ...thStyle, width: 76, textAlign: "right" }}>{t("locations.list.actions", "Actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {sorted.map((l, i) => {
                    const tz = str(l, "timezone");
                    const on = !!sel[l.id];
                    return (
                      <tr key={l.id} data-id={l.id} onClick={() => actions(l).onView()} style={{ borderTop: i ? `1px solid ${T.border}` : "none", cursor: "pointer", background: on ? T.cardSoft : undefined }}>
                        <td style={tdStyle} onClick={(e) => e.stopPropagation()}>
                          <Checkbox checked={on} onChange={() => toggleSel(l.id)} aria-label={`${t("locations.list.select", "Select")} ${l.name}`} />
                        </td>
                        <td style={tdStyle}>
                          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                            <span style={{ width: 32, height: 32, borderRadius: 6, background: T.p3, color: T.p1, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><Icon.loc size={14} /></span>
                            <div style={{ minWidth: 0 }}>
                              <div style={{ color: T.n1, fontWeight: 600 }}>{l.name}</div>
                              <div style={{ fontSize: 11.5, color: T.soft, marginTop: 1, display: "flex", gap: 8, alignItems: "baseline" }}>
                                {str(l, "externalCode") ? <span style={{ fontFamily: T.fontMono }}>{str(l, "externalCode")}</span> : null}
                                {/* Staff folds in here when the column is dropped below 1100px. */}
                                {narrow ? <span>{staffAt(l)} {t("locations.list.staff", "Staff").toLowerCase()}</span> : null}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td style={{ ...tdStyle, color: T.n2, minWidth: 0, overflow: "hidden" }}>
                          <div style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{[str(l, "address"), str(l, "addressLine2")].filter(Boolean).join(", ") || l.address}</div>
                          <div style={{ fontSize: 11.5, color: T.soft, marginTop: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                            {[str(l, "city"), str(l, "region"), str(l, "postalCode")].filter(Boolean).join(", ")}
                            {str(l, "country") ? ` · ${str(l, "country")}` : ""}
                          </div>
                        </td>
                        <td style={tdStyle}>
                          {tz ? (
                            <>
                              <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
                                <span style={{ fontSize: T.text.body.fontSize, fontWeight: 600, color: T.n1, fontVariantNumeric: "tabular-nums" }}>{zoneClock(tz)}</span>
                                <span style={{ fontSize: 11, fontFamily: T.fontMono, color: T.soft }}>{zoneOffset(tz)}</span>
                              </div>
                              <div style={{ fontSize: 11.5, color: T.soft, marginTop: 1 }}>{tz}</div>
                            </>
                          ) : <Pill tone="danger" dot>{t("locations.list.noTimeZone", "No time zone")}</Pill>}
                        </td>
                        <td style={tdStyle}>{openPill(l)}</td>
                        {narrow ? null : <td style={{ ...tdStyle, color: T.n1 }}>{staffAt(l)}</td>}
                        <td style={{ ...tdStyle, textAlign: "right" }} onClick={(e) => e.stopPropagation()}>
                          <RowActions {...actions(l)} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </Card>
          )}
        </div>
      </div>
    </Screen>
  );
}
